import { UserPlus } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

/**
 * Extracted from `AdminUsersPanel` (issue #626 — that component was 47 lines
 * over the `max-lines-per-function` budget). Pure markup: every piece of
 * state (`form`), the submit handler (`createUser`) and the one-time
 * password it produces all still live in `AdminUsersPanel`. This component
 * only renders the "Create an account" card and reports field edits and the
 * submit event upward.
 */
export const ROLE_OPTIONS = [
  { value: "player", label: "Player" },
  { value: "coach", label: "Coach" },
];

export default function AdminCreateUserForm({ form, onFieldChange, onSubmit, submitting, canSubmit }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus size={16} color="#0B2E59" />
          <h3 className="font-display font-bold text-slate-900">Create an account</h3>
        </div>
        <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-create-form">
          {/* min-w-0 on every grid child — #251: a grid item defaults to
              min-width:auto and an input's intrinsic width then pushes the
              whole page sideways at 390px. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="min-w-0">
              <Label htmlFor="admin-new-name">Name</Label>
              <Input
                id="admin-new-name"
                value={form.display_name}
                onChange={(e) => onFieldChange("display_name", e.target.value)}
                placeholder="Jo Bloggs"
                className="mt-1"
                data-testid="admin-new-name"
              />
            </div>
            <div className="min-w-0">
              <Label htmlFor="admin-new-email">Email</Label>
              <Input
                id="admin-new-email"
                type="email"
                value={form.email}
                onChange={(e) => onFieldChange("email", e.target.value)}
                placeholder="jo@example.com"
                className="mt-1"
                data-testid="admin-new-email"
              />
            </div>
            <div className="min-w-0">
              <Label htmlFor="admin-new-role">Role</Label>
              <Select value={form.role} onValueChange={(v) => onFieldChange("role", v)}>
                <SelectTrigger id="admin-new-role" className="mt-1" data-testid="admin-new-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-0">
              <Label htmlFor="admin-new-dob">Date of birth (optional)</Label>
              <Input
                id="admin-new-dob"
                type="date"
                value={form.date_of_birth}
                onChange={(e) => onFieldChange("date_of_birth", e.target.value)}
                className="mt-1"
                data-testid="admin-new-dob"
              />
              <p className="text-[11px] text-slate-500 mt-1 break-words">
                Players only. Sign-up requires it; leave blank if you don't have it and the account
                will be listed as "age unknown" until someone fills it in.
              </p>
            </div>
          </div>

          <p className="text-xs text-slate-500 break-words">
            A password is generated for you and shown once, here, after you create the account.
            You can't create another admin from this screen.
          </p>

          <Button
            type="submit"
            disabled={!canSubmit}
            className="w-full sm:w-auto"
            style={{ background: "#0B2E59" }}
            data-testid="admin-create-submit"
          >
            {submitting ? "Creating…" : "Create account"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
